import { Star, MapPin, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { RateHawkSearchResult } from "../types";
import { WorldotaImage } from "../WorldotaImage";
import { formatCurrency } from "./helpers";

export function SearchResultCardSkeleton() {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-row">
      <Skeleton className="w-40 h-28 shrink-0 rounded-none" />
      <div className="flex-1 p-3 flex flex-col gap-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-2/3" />
      </div>
      <div className="shrink-0 p-3 flex flex-col items-end justify-between border-l border-gray-100 min-w-[140px]">
        <div className="flex flex-col items-end gap-1.5 w-full">
          <Skeleton className="h-3 w-8" />
          <Skeleton className="h-5 w-20" />
        </div>
        <Skeleton className="h-8 w-full mt-2" />
      </div>
    </div>
  );
}

interface SearchResultCardProps {
  result: RateHawkSearchResult;
  onSelect: (r: RateHawkSearchResult) => void;
}

export function SearchResultCard({ result, onSelect }: SearchResultCardProps) {
  const photo = result.coverPhoto;
  const starRating = result.starRating;
  const city = result.cityName;
  const pt = result.cheapestRate?.payment_options?.payment_types?.[0];
  const priceAmount = result.cheapestShowAmount || result.cheapestRate?.show_amount || pt?.show_amount;
  const priceCurrency = result.cheapestShowCurrency || result.cheapestRate?.show_currency_code || pt?.show_currency_code;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all flex flex-row">
      {/* Photo - left */}
      <div className="w-40 h-28 shrink-0 overflow-hidden">
        <WorldotaImage
          url={photo}
          alt={result.name}
          className="w-full h-full"
        />
      </div>

      {/* Info - center */}
      <div className="flex-1 p-3 flex flex-col gap-1 min-w-0 overflow-hidden">
        <h3 className="text-base font-medium text-gray-900 line-clamp-1">{result.name}</h3>
        {city && (
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <MapPin className="h-3 w-3 shrink-0" />
            {city}
          </p>
        )}
        {result.description && (
          <p className="text-sm text-gray-600 overflow-hidden text-ellipsis line-clamp-2">
            {result.description}
          </p>
        )}
        {starRating != null && starRating > 0 && (
          <span className="flex items-center gap-0.5 text-amber-600 mt-auto">
            {Array.from({ length: starRating }).map((_, i) => (
              <Star key={i} className="h-3 w-3 text-amber-400 fill-amber-400" />
            ))}
          </span>
        )}
      </div>

      {/* Price + action - right */}
      <div className="shrink-0 p-3 flex flex-col items-end justify-between border-l border-gray-100 min-w-[140px]">
        <div className="text-right">
          <p className="text-xs text-gray-500">From</p>
          {priceAmount ? (
            <p className="text-base font-semibold text-blue-600">
              {formatCurrency(priceAmount, priceCurrency)}
            </p>
          ) : (
            <p className="text-gray-400">Price on request</p>
          )}
        </div>
        <Button
          onClick={() => onSelect(result)}
          size="sm"
          className="mt-2 w-full"
        >
          Select a room
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
